import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchHealth, sendIssuePreview } from "../lib/api";
import { localImageCount, type PreviewSection } from "../lib/newsletter-html";
import type { IssueDetail } from "../lib/segment-types";
import { SecondaryButton, StatusBadge } from "./primitives";

const LAST_TO_KEY = "nl-preview-to";

function lastTo(): string {
  try {
    return localStorage.getItem(LAST_TO_KEY) ?? "";
  } catch {
    return "";
  }
}

export function SendTestBox({
  issue,
  sections,
  categoryHeading,
  prominent,
}: {
  issue?: Pick<IssueDetail, "title" | "subject" | "notes" | "articles_list" | "blocks" | "preheader" | "utm" | "sender_name" | "sections">;
  /**
   * The sections with their per-reader slots already filled, so the test
   * email is the one the preview was showing rather than a second rendering
   * that resolves differently.
   */
  sections?: PreviewSection[];
  categoryHeading?: string;
  prominent?: boolean;
}) {
  const health = useQuery({ queryKey: ["health"], queryFn: fetchHealth });
  const [to, setTo] = useState(lastTo);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [detail, setDetail] = useState("");

  const connected = health.data?.email === true;
  const from = health.data?.email_from || "newsletter@altibbi.me";
  const title = issue?.title.trim() || "Email Service connection test";
  const canSend = connected && title.length > 0;
  const skippedPhotos = localImageCount(issue?.blocks, sections ?? (issue?.sections as PreviewSection[] | undefined));

  async function send() {
    setStatus("sending");
    setDetail("");
    try {
      const result = await sendIssuePreview({
        to,
        title,
        subject: issue?.subject ?? "Your Cloudflare sender is connected",
        articles: issue?.articles_list ?? [],
        blocks: issue?.blocks ?? [],
        // The body is built from sections. Leaving them out is what made a
        // test send arrive as an empty newsletter.
        sections: sections ?? (issue?.sections as PreviewSection[] | undefined),
        category_heading: categoryHeading,
        notes: issue?.notes,
        preheader: issue?.preheader,
        sender_name: issue?.sender_name,
        utm: issue?.utm,
      });
      try { localStorage.setItem(LAST_TO_KEY, to.trim()); } catch { /* ignore */ }
      setStatus("sent");
      setDetail(`Sent to ${result.to} from ${result.from}`);
    } catch (err) {
      setStatus("error");
      setDetail(err instanceof Error ? err.message : "Send failed");
    }
  }

  return (
    <div className={`nl-send-test${prominent ? " is-prominent" : ""}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div className="nl-stat-mini-label">Send test email</div>
        <StatusBadge tone={connected ? "success" : health.isLoading ? "muted" : "warn"}>
          {connected ? "Test sending ready" : health.isLoading ? "Checking…" : "Test sending unavailable"}
        </StatusBadge>
      </div>
      <p>
        Sends this email to your own inbox only, from <strong>{from}</strong>. Subscribers never receive a test.
      </p>
      {skippedPhotos > 0 && (
        <p style={{ color: "var(--c-warn-text)" }}>
          {skippedPhotos === 1 ? "1 photo" : `${skippedPhotos} photos`} uploaded from this computer will not appear in the inbox. Paste an https image URL in the photo block.
        </p>
      )}
      {!connected && !health.isLoading && (
        <p style={{ color: "var(--c-warn-text)" }}>
          Start the Worker on port 8787 with the Cloudflare Email binding, then retry.
        </p>
      )}
      <label className="nl-field">
        <span>Send preview to</span>
        <input
          className="nl-input"
          type="email"
          autoComplete="email"
          placeholder="you@altibbi.com"
          value={to}
          onChange={(e) => { setTo(e.target.value); setStatus("idle"); }}
        />
      </label>
      <SecondaryButton
        disabled={!canSend || status === "sending" || !to.trim()}
        title={!connected ? "Email Service is offline" : !title ? "Add a title first" : "Send a one-off preview via Cloudflare"}
        onClick={() => void send()}
      >
        {status === "sending" ? "Sending…" : "Send test"}
      </SecondaryButton>
      {detail && (
        <div style={{
          fontSize: "var(--ui-text-meta)",
          color: status === "error" ? "var(--c-warn-text)" : "var(--c-success-text)",
        }}>
          {detail}
        </div>
      )}
    </div>
  );
}
